import keys from '@debbie/common/dist/cache/keys.js';
import { UnknownIntent, } from '@debbie/common/dist/intents/index.js';
import { TemplateType } from '@debbie/common/dist/templates/index.js';
import { getMentionSuggestions, toEditorMentions, } from '@debbie/common/dist/templates/suggestions.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as templatesAPI from '@debbie/cortex/dist/api/templates.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import q from '@debbie/cortex/dist/query.js';
import { templatesEntity } from '@debbie/cortex/dist/templates/index.js';
import { useTenant } from '@debbie/cortex/dist/tenant/index.js';
import { useUpdateTypes } from '@debbie/cortex/dist/update-types/index.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../../theme.js';
import { errorNotification } from '../../../utils/errors.js';
import { encodeText } from '../../../utils/templates.js';
import AutoCallActions from '../../AutoCallActions.js';
import NewEditor from '../../editor/Editor.js';
import { useEditorState } from '../../editor/hooks.js';
import { ExclusiveContentBadge } from '../../ExclusiveContent.js';
import { ExternalLink } from '../../Icons/index.js';
import { Button, ExclusiveContent, Intent, Panel } from '../../index.js';
import { Section } from '../../Panel.js';
import { AddTagModal, CopyTemplateModal, DocxTemplateSection, Header, PreviewModal, SettingsModal, VersionsModal, } from './index.js';
const TemplateEditor = (props) => {
    const lang = useSelector((s) => s.lang);
    const editorRef = useRef(null);
    const updateTypesLoadable = useUpdateTypes();
    const updateTypes = h.hasData(updateTypesLoadable)
        ? updateTypesLoadable.data
        : null;
    const tenant = useTenant();
    const propertyTypesLoadable = usePropertyTypes();
    const propertyTypes = h.hasData(propertyTypesLoadable)
        ? propertyTypesLoadable.data
        : undefined;
    const [state, setState] = useState({
        defaultTemplate: null,
        defaultContent: '<p></p>',
        defaultSubject: '<p></p>',
        defaultIntent: UnknownIntent(),
        htmlContent: '<p></p>',
        htmlSubject: '<p></p>',
        intent: UnknownIntent(),
        template: null,
        loading: false,
        settingsModalOpen: false,
        previewModalOpen: false,
        versionsModalOpen: false,
        copyTemplateModalOpen: false,
        addTagModalOpen: false,
        changesNotSaved: false,
        error: null,
    });
    const subjectEditorState = useEditorState(state.htmlSubject);
    const contentEditorState = useEditorState(state.htmlContent);
    const deleteTemplateMutation = useMutation(async () => {
        await templatesAPI.del(props.templateId);
        q.invalidate(keys.write.modify(templatesEntity, props.templateId));
        c.redirect.redirect('/settings/template-editor');
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'delete', lang.errorEntities.template);
        },
    });
    useEffect(() => {
        // @ts-ignore
        editorRef.current?.focusEditor();
        if (h.hasData(propertyTypesLoadable) || h.isError(propertyTypesLoadable)) {
            loadTemplate(props);
        }
    }, [
        props.templateId,
        props.selectedLanguage,
        propertyTypesLoadable.loadingState,
    ]);
    useEffect(() => {
        checkChangesState();
    }, [state]);
    const mentionSuggestions = useMemo(() => state.template
        ? getMentionSuggestions(lang, state.template.type, propertyTypes, tenant)
        : [], [lang, state.template?.type, propertyTypes, tenant]);
    const loadTemplate = async (props) => {
        if (!state.loading) {
            setState((state) => ({ ...state, loading: true }));
            try {
                const template = await templatesAPI.getTemplate(props.templateId, props.ac, props.selectedLanguage, true);
                const mentionSuggestions = getMentionSuggestions(lang, template.type, propertyTypes || undefined, tenant);
                const latest = template;
                const subject = encodeText(latest.subject || '<p></p>', mentionSuggestions);
                const content = encodeText(latest.html || '<p></p>', mentionSuggestions);
                const intent = latest.intent;
                // const subjectValidationErrors = templateValidator(subject, lang)
                // const editorValidationErrors = templateValidator(editorState, lang)
                setState((state) => ({
                    ...state,
                    intent,
                    htmlContent: content,
                    htmlSubject: subject,
                    loading: false,
                    template: latest,
                    defaultTemplate: latest,
                    defaultContent: content,
                    defaultSubject: subject,
                    defaultIntent: intent,
                    // subjectValidationErrors,
                    // editorValidationErrors,
                }));
                subjectEditorState.overwriteEditorContent(subject);
                contentEditorState.overwriteEditorContent(content);
            }
            catch (e) {
                setState((state) => ({ ...state, loading: false, error: e?.message }));
            }
        }
    };
    const checkChangesState = () => {
        const settingsAreChanged = JSON.stringify(state.template) !== JSON.stringify(state.defaultTemplate);
        if (state.defaultContent == null || state.defaultSubject == null) {
            return;
        }
        const editorIsChanged = state.htmlContent !== state.defaultContent;
        const subjectIsChanged = state.htmlSubject !== state.defaultSubject;
        const intentChanged = JSON.stringify(state.intent) !== JSON.stringify(state.defaultIntent);
        const changesNotSaved = settingsAreChanged || editorIsChanged || subjectIsChanged || intentChanged;
        if (changesNotSaved !== state.changesNotSaved) {
            // const subjectValidationErrors = templateValidator(
            //   state.subjectState,
            //   lang,
            // )
            // const editorValidationErrors = templateValidator(state.editorState, lang)
            setState((state) => ({
                ...state,
                changesNotSaved: changesNotSaved,
                // subjectValidationErrors,
                // editorValidationErrors,
            }));
        }
    };
    const loadInEditor = (template) => {
        const content = encodeText(template.html || '', mentionSuggestions);
        // const editorValidationErrors = templateValidator(editorState, lang)
        setState((state) => ({
            ...state,
            template: template,
            htmlContent: content,
            // editorValidationErrors,
        }));
        contentEditorState.overwriteEditorContent(content);
    };
    const onEditorStateChange = (content) => {
        // const editorValidationErrors = templateValidator(editorState, lang)
        // setState({ ...state, editorState: editorState, editorValidationErrors })
        contentEditorState.onChange(content);
        setState((state) => ({ ...state, htmlContent: content }));
    };
    const onSubjectStateChange = (subject) => {
        // const subjectValidationErrors = templateValidator(editorState, lang)
        // setState({ ...state, subjectState: editorState, subjectValidationErrors })
        subjectEditorState.onChange(subject);
        setState((state) => ({ ...state, htmlSubject: subject }));
    };
    const onIntentChange = (intent) => {
        setState((state) => ({ ...state, intent }));
    };
    const editorMentions = state.template
        ? toEditorMentions(mentionSuggestions)
        : [];
    const inUpdateTypes = updateTypes &&
        updateTypes.filter((updateType) => updateType.procedure?.find(
        // @ts-ignore
        (todo) => JSON.stringify(todo.details).indexOf(props.templateId) !== -1));
    return (React.createElement("div", null, state.error ? (React.createElement(Panel, { header: "Error getting template" }, state.error)) : (React.createElement("div", null,
        state.template && (React.createElement(Header, { mentionSuggestions: mentionSuggestions, templateId: props.templateId, selectedLanguage: props.selectedLanguage, ac: props.ac, template: state.template, setTemplate: (template) => {
                setState((state) => ({ ...state, template }));
                loadTemplate(props);
            }, subject: state.htmlSubject, content: state.htmlContent, intent: state.intent, changesNotSaved: state.changesNotSaved, toggleModal: (name) => {
                setState((state) => ({
                    ...state,
                    // Find a better solution for better types
                    //@ts-ignore
                    [`${name}ModalOpen`]: !state[`${name}ModalOpen`],
                }));
            }, loading: state.loading, defaultTemplate: state.defaultTemplate })),
        React.createElement("br", null),
        state.template &&
            [TemplateType.EMAIL, TemplateType.EBOKS].includes(state.template.type) && (React.createElement(EmailSubjectEditor, null,
            React.createElement(NewEditor, { hideControls: true, compact: true, singleLine: true, placeholder: lang.interactions.subject, mentions: editorMentions, initialContent: subjectEditorState.editorContent, onChange: (html) => onSubjectStateChange(html) }))),
        React.createElement("br", null),
        React.createElement(NewEditor, { mentions: editorMentions, onChange: (html) => onEditorStateChange(html), initialContent: contentEditorState.editorContent }),
        React.createElement("br", null),
        state.template && state.template.type === TemplateType.AUTOCALL && (React.createElement("div", null,
            React.createElement(AutoCallActions, { actions: state.template.details.actions || [], onChange: (actions) => {
                    setState((state) => {
                        if (!state.template) {
                            return state;
                        }
                        return {
                            ...state,
                            template: {
                                ...state.template,
                                details: {
                                    ...state.template.details,
                                    actions,
                                },
                            },
                        };
                    });
                } }),
            React.createElement("p", { onClick: () => {
                    if (state.template &&
                        state.template.type === TemplateType.AUTOCALL) {
                        setState((state) => {
                            if (state.template == null ||
                                state.template.type !== TemplateType.AUTOCALL) {
                                return state;
                            }
                            return {
                                ...state,
                                template: {
                                    ...state.template,
                                    details: {
                                        ...state.template.details,
                                        actions: (state.template.details.actions || []).concat({
                                            procedureId: 'f892b601-2d51-46e6-901c-1b90556dcdf3', //Empty procedure
                                            id: Math.random().toLocaleString(),
                                        }),
                                    },
                                },
                            };
                        });
                    }
                }, style: {
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    fontWeight: 900,
                    color: THEME.COLOR_ACCENT,
                    marginTop: '0px',
                } }, lang.autoCall.addAction))),
        state.intent && (React.createElement(Panel, null,
            React.createElement(Intent, { intent: state.intent, editable: !state.defaultTemplate?.public, onEdit: (intent) => {
                    onIntentChange(intent);
                } }))),
        state.template &&
            [TemplateType.LETTER, TemplateType.EBOKS].includes(state.template.type) && (React.createElement(ExclusiveContent, null,
            React.createElement(ExclusiveContentBadge, null),
            React.createElement(DocxTemplateSection, { template: state.template, setTemplate: (template) => setState((state) => ({ ...state, template })), mentionSuggestions: mentionSuggestions }))),
        inUpdateTypes && !!inUpdateTypes.length && (React.createElement(Panel, null,
            React.createElement(Section, { header: lang.templates.updateTypesUsingThisTemplate, description: lang.templates.updateTypesUsingThisTemplate },
                React.createElement("ul", null, inUpdateTypes.map((updateType) => (React.createElement("li", { key: updateType.updateTypeId, style: {
                        cursor: 'pointer',
                    }, onClick: () => c.redirect.redirect(`/settings/workflows/tasks/${updateType.updateTypeId}`) },
                    updateType.label,
                    ' ',
                    React.createElement(ExternalLink, { style: {
                            margin: '0.1em 0 -0.1em',
                        } })))))))),
        React.createElement(Button, { size: "small", theme: "dangerous", disabled: (inUpdateTypes?.length || 0) > 0 || props.preventDelete, loading: deleteTemplateMutation.isLoading, onClick: () => {
                if (window.confirm(lang.templates.confirmDelete)) {
                    deleteTemplateMutation.mutate();
                }
            } }, lang.templates.delete),
        state.previewModalOpen && (React.createElement(PreviewModal, { toggleModal: () => setState((state) => ({
                ...state,
                previewModalOpen: !state.previewModalOpen,
            })), templateId: props.templateId, template: state.template, content: state.htmlContent, subject: state.htmlSubject, caseId: props.caseId, setCaseId: props.setCaseId })),
        state.settingsModalOpen && state.template && (React.createElement(SettingsModal, { toggleSettingsModal: () => setState((state) => ({
                ...state,
                settingsModalOpen: !state.settingsModalOpen,
            })), template: state.template, setTemplate: (template) => {
                setState((state) => ({ ...state, template }));
            } })),
        state.versionsModalOpen && state.template && (React.createElement(VersionsModal, { toggleVersionsModal: () => setState((state) => ({
                ...state,
                versionsModalOpen: !state.versionsModalOpen,
            })), ac: props.ac, loadInEditor: loadInEditor, selectedLanguage: props.selectedLanguage, template: state.template, templateId: props.templateId, defaultTemplate: state.defaultTemplate })),
        state.addTagModalOpen && (React.createElement(AddTagModal, { templateId: props.templateId, toggleTagModal: async () => setState((state) => ({
                ...state,
                addTagModalOpen: !state.addTagModalOpen,
            })), loadTemplate: async () => {
                await loadTemplate(props);
            } })),
        state.copyTemplateModalOpen && state.template && (React.createElement(CopyTemplateModal, { toggleModal: () => setState((state) => ({
                ...state,
                copyTemplateModalOpen: !state.copyTemplateModalOpen,
            })), loadTemplate: () => loadTemplate(props), template: state.template, templateId: props.templateId }))))));
};
export default TemplateEditor;
const EmailSubjectEditor = styled.div `
  .rdw-editor-toolbar {
    display: none;
    position: sticky;
    top: 0px;
    z-index: 999;
  }
  .rdw-editor-wrapper {
    .rdw-editor-main {
      min-height: 45px;
      border-top: 1px solid ${THEME.BORDER_STRONG};
      border-top-left-radius: 3px;
      border-top-right-radius: 3px;

      .public-DraftStyleDefault-block {
        margin: 0;
      }
    }
  }
`;
//# sourceMappingURL=TemplateEditor.js.map